import api from "@/plugins/api";
import { authState } from "@/plugins/keycloak";
import { logger } from "@/infrastructure/services/LoggerService";
import { mapResponseToUserListItem } from "../mappers/UserListMapper";
import { mapResponseToManagedUser } from "../mappers/UserMapper";
/**
 * Admin user management via the BFF. Lot 1 implements the read methods consumed by the
 * list (this lot) and detail (Lot 2) views. Read methods (list/getById) feed the list and detail
 * views; the action methods below wire the Lot 4 admin actions (deactivate/reactivate,
 * reset password, resend invitation) onto the merged BFF `POST /users/{id}/*` routes. Each
 * returns nothing on success and lets the axios error propagate so the store/composable can
 * map the BFF `errorCode` to a localized toast.
 */
export class UsersAdminService {
    async list(params) {
        try {
            const query = {
                pageNumber: params.pageNumber,
                pageSize: params.pageSize,
            };
            if (params.sortBy)
                query.sortBy = params.sortBy;
            if (params.sortDirection)
                query.sortDirection = params.sortDirection;
            if (params.search)
                query.search = params.search;
            if (params.name)
                query.name = params.name;
            if (params.email)
                query.email = params.email;
            if (params.roleTypes?.length)
                query.roleTypes = params.roleTypes;
            if (params.states?.length)
                query.states = params.states;
            if (params.environmentIds?.length)
                query.environmentIds = params.environmentIds;
            if (params.lastLoginFrom)
                query.lastLoginFrom = params.lastLoginFrom;
            if (params.lastLoginBefore)
                query.lastLoginBefore = params.lastLoginBefore;
            // Raw BFF body is { success, data: { userList: {...} } }; the api interceptor
            // unwraps it to response.data = { userList: {...} }. pageSize is not echoed by
            // the BFF, so it is carried through from the request.
            const response = await api.get("/users", {
                unwrapEnvelope: true,
                params: query,
                // The repeated `roleTypes=` form is what ASP.NET binds; axios' default `[]=` is not.
                paramsSerializer: { indexes: null },
            });
            const userList = response.data?.userList ?? {};
            return {
                items: (userList.items ?? []).map(mapResponseToUserListItem),
                pageNumber: userList.pageNumber ?? params.pageNumber,
                pageSize: params.pageSize,
                totalCount: userList.totalCount ?? 0,
                totalPages: userList.totalPages ?? 0,
            };
        }
        catch (error) {
            logger.debug("Failed to list users:", error);
            throw error;
        }
    }
    async getById(userId) {
        try {
            const response = await api.get(`/users/${userId}/details`, {
                unwrapEnvelope: true,
            });
            return mapResponseToManagedUser(response.data);
        }
        catch (error) {
            logger.debug("Failed to get user details:", error);
            throw error;
        }
    }
    /** Updates only a user's roles — never touches profile fields or instance assignments. */
    async updateRoles(userId, roles) {
        try {
            await api.put(`/users/${userId}/roles`, { roles }, { unwrapEnvelope: true });
        }
        catch (error) {
            logger.debug("Failed to update user roles:", error);
            throw error;
        }
    }
    /** Deactivate a user for the connected client (reversible; role mappings preserved). */
    async deactivate(userId) {
        try {
            await api.post(`/users/${userId}/deactivate`, undefined, { unwrapEnvelope: true });
        }
        catch (error) {
            logger.debug("Failed to deactivate user:", error);
            throw error;
        }
    }
    /** Reactivate a previously deactivated user for the connected client. */
    async reactivate(userId) {
        try {
            await api.post(`/users/${userId}/reactivate`, undefined, { unwrapEnvelope: true });
        }
        catch (error) {
            logger.debug("Failed to reactivate user:", error);
            throw error;
        }
    }
    /**
     * Send a password-reset email to the user via Keycloak. The redirect target is passed so that
     * Keycloak links the user back to this portal once the new password is set — without it the
     * action email has no link back and the user is left on the Keycloak account console.
     */
    async resetPassword(userId) {
        try {
            await api.post(`/users/${userId}/reset-password`, {
                redirectUri: `${window.location.origin}/`,
                clientId: authState.keycloakInstance.clientId,
            }, { unwrapEnvelope: true });
        }
        catch (error) {
            logger.debug("Failed to reset user password:", error);
            throw error;
        }
    }
    /** Re-emit the invitation email for a user who holds a pending invitation. */
    async resendInvitation(userId) {
        try {
            await api.post(`/users/${userId}/resend-invitation`, undefined, { unwrapEnvelope: true });
        }
        catch (error) {
            logger.debug("Failed to resend user invitation:", error);
            throw error;
        }
    }
    /**
     * Invite a batch of existing users (already present in Keycloak) into the connected
     * organisation, granting each of them the same roles. The BFF resolves the organisation
     * from the caller's JWT — it is never supplied by the frontend.
     *
     * Returns 200 even on a mixed or fully-refused outcome: per-address refusals live in
     * `failed`, so callers must read the envelope rather than rely on the throw.
     */
    async inviteUsers(emails, roles) {
        try {
            // The api plugin's BFF interceptor already unwraps the ApiResponse envelope, so
            // response.data IS the result model — there is no second `.data` hop here.
            const response = await api.post("/users/invite", {
                emails,
                roles,
                // Only used for an address with no Keycloak account yet: the invitee completes their
                // profile, chooses a password, and is returned here. Keycloak honours the return trip only
                // when both halves are present — same contract as resetPassword above.
                redirectUri: `${window.location.origin}/`,
                clientId: authState.keycloakInstance.clientId,
            }, { unwrapEnvelope: true });
            return response.data;
        }
        catch (error) {
            logger.debug("Failed to invite users:", error);
            throw error;
        }
    }
}
